import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Search, MoreHorizontal, MoreVertical, Share2, Pencil, Trash2, ArrowUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import useStore from '@/store/useStore';
import { useAuth } from '@/contexts/AuthContext';
import { EntityActions } from '@/components/crud/EntityActions';
import ViewToggle from '@/components/common/ViewToggle';
import BulkImport from '@/components/common/BulkImport';
import SortableTableHeader from '@/components/common/SortableTableHeader';
import UniqueIdDisplay from '@/components/common/UniqueIdDisplay';
import BulkActions from '@/components/common/BulkActions';
import { Tournament } from '@/types/cricket';
import { defaultColors } from '@/components/ui/ColorGridPicker';
import { cn, generateHash } from '@/lib/utils';
import { getRandomColor } from '@/store/useStore';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from '@/components/ui/use-toast';
import { debugTournaments } from '@/services/supabaseService';

const TournamentsPage = () => {
  const { tournaments, loading, fetchTournaments, updateTournament, deleteTournament, addTournament, matches, fetchMatches } = useStore();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [view, setView] = useState<'card' | 'list'>('card');
  const [sortKey, setSortKey] = useState<string | null>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [selectedTournaments, setSelectedTournaments] = useState<string[]>([]);

  useEffect(() => {
    if (user) {
      fetchTournaments();
      fetchMatches();
      // Temporary debug call
      debugTournaments().then(result => {
        console.log('Debug result:', result);
      }).catch(error => {
        console.error('Debug error:', error);
      });
    }
  }, [user, fetchTournaments, fetchMatches]);

  console.log('Tournaments from store:', tournaments);

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  const sortedTournaments = React.useMemo(() => {
    const filtered = tournaments.filter(tournament =>
      tournament.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tournament.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (tournament.uniqueId && tournament.uniqueId.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    if (sortKey) {
      filtered.sort((a, b) => {
        let aValue: string | number | Date | undefined;
        let bValue: string | number | Date | undefined;
    
        if (sortKey === 'createdAt' || sortKey === 'updatedAt' || sortKey === 'startDate') {
          aValue = a[sortKey] ? new Date(a[sortKey]) : new Date(0);
          bValue = b[sortKey] ? new Date(b[sortKey]) : new Date(0);
          return sortDirection === 'asc' ? (aValue as Date).getTime() - (bValue as Date).getTime() : (bValue as Date).getTime() - (aValue as Date).getTime();
        } else {
          aValue = a[sortKey as keyof Tournament] as string | number | undefined;
          bValue = b[sortKey as keyof Tournament] as string | number | undefined;
        }

        if (aValue === undefined || aValue === null) aValue = '';
        if (bValue === undefined || bValue === null) bValue = '';

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
        }
    
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        }
    
        return 0;
      });
    }

    return filtered;
  }, [tournaments, searchTerm, sortKey, sortDirection]);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedTournaments(sortedTournaments.map(t => t.id));
    } else {
      setSelectedTournaments([]);
    }
  };

  const handleSelectTournament = (tournamentId: string, checked: boolean) => {
    if (checked) {
      setSelectedTournaments([...selectedTournaments, tournamentId]);
    } else {
      setSelectedTournaments(selectedTournaments.filter(id => id !== tournamentId));
    }
  };

  const handleBulkDelete = async () => {
    for (const tournamentId of selectedTournaments) {
      try {
        await deleteTournament(tournamentId);
      } catch (error) {
        console.error('Error deleting tournament:', error);
      }
    }
    setSelectedTournaments([]);
  };

  const handleBulkEdit = () => {
    // Implement bulk edit functionality
    console.log('Bulk edit tournaments:', selectedTournaments);
  };

  const handleBulkImport = async (data: { name: string; location: string; startDate: string }[]) => {
    for (const row of data) {
      try {
        await addTournament({
          name: row.name || '',
          location: row.location || '',
          startDate: row.startDate || new Date().toISOString(),
        });
      } catch (error) {
        console.error('Error importing tournament:', error);
      }
    }
  };

  const sampleTournamentData = [
    { name: 'Summer Cricket League', location: 'New York', startDate: '2024-06-01' }
  ];

  const csvHeaders = ['name', 'location', 'startDate'];

  if (!user) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold mb-4">Please sign in</h2>
        <p>You need to be signed in to view tournaments.</p>
      </div>
    );
  }

  if (loading.tournaments) {
    return (
      <div className="text-center py-12">
        <p>Loading tournaments...</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
        <h1 className="text-2xl font-bold">Tournaments</h1>
        <div className="flex items-center gap-2">
          {/* Desktop buttons */}
          <BulkImport type="tournaments" onImport={handleBulkImport} sampleData={sampleTournamentData} headers={csvHeaders} />
          <Link to="/tournaments/new">
            <Button className="bg-cricket-green">Create Tournament</Button>
          </Link> 
          {/* Mobile three-dots menu */}
          <div className="sm:hidden">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="btn p-2" aria-label="More actions">
                  <MoreVertical />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => handleBulkImport(sampleTournamentData)}>Bulk Import</DropdownMenuItem>
                <DropdownMenuItem onClick={() => console.log('Create Tournament')}>Create Tournament</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder="Search tournaments..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {['name', 'createdAt', 'updatedAt'].map(key => (
                  <DropdownMenuItem key={key} onSelect={() => handleSort(key)}>
                    <span className="capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                    {sortKey === key && <ArrowUp className={`ml-auto h-4 w-4 ${sortDirection === 'desc' ? 'transform rotate-180' : ''}`} />}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')}>
                  Toggle Sort Direction
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
        <ViewToggle view={view} onViewChange={setView} />
        </div>
      </div>

      {selectedTournaments.length > 0 && view === 'list' && (
        <BulkActions
          selectedCount={selectedTournaments.length}
          onDelete={handleBulkDelete}
          onEdit={handleBulkEdit}
          entityType="tournament"
        />
      )}

      {sortedTournaments.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <h3 className="text-xl font-semibold mb-2">No tournaments yet</h3>
            <p className="text-gray-600 mb-4">Create your first tournament to get started!</p>
            <Link to="/tournaments/new">
              <Button className="bg-cricket-green">Create Tournament</Button>
            </Link>
          </CardContent>
        </Card>
      ) : view === 'card' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sortedTournaments.map((tournament) => {
            const matchesCount = matches.filter(m => m.tournamentId === tournament.id).length;
            return (
              <Card key={tournament.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between flex-wrap min-w-0">
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      {tournament.logoUrl ? (
                        <img
                          src={tournament.logoUrl}
                          alt={tournament.name + ' logo'}
                          className="w-20 h-20 object-cover rounded-full border-2 border-gray-200 bg-white shadow-sm"
                        />
                      ) : (
                        <div
                          className="w-20 h-20 rounded-full border-2 border-gray-200 flex items-center justify-center shadow-sm"
                          style={{
                            backgroundColor: defaultColors[
                              Math.abs(generateHash(tournament.id || tournament.name || '')) % defaultColors.length
                            ]
                          }}
                        >
                          <span className="text-4xl font-bold text-white drop-shadow-md select-none">
                            {tournament.name?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                        </div>
                      )}
                      <div className="flex flex-col justify-center min-w-0">
                        <CardTitle className="text-xl">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="block truncate max-w-[220px] cursor-pointer">{tournament.name}</span>
                            </TooltipTrigger>
                            <TooltipContent>{tournament.name}</TooltipContent>
                          </Tooltip>
                        </CardTitle>
                        <CardDescription className="mt-1">{tournament.location}</CardDescription>
                        {tournament.uniqueId && (
                          <UniqueIdDisplay uniqueId={tournament.uniqueId} className="mt-2" />
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end self-start">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="p-1 rounded hover:bg-muted transition-colors" title="More actions">
                            <MoreVertical size={20} />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => {
                              const url = `${window.location.origin}/tournaments/${tournament.id}`;
                              navigator.clipboard.writeText(url);
                              toast({ title: 'Tournament link copied!', description: 'Tournament URL copied to clipboard.' });
                            }}
                          >
                            <Share2 size={16} className="mr-2" /> Share
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => document.getElementById(`edit-tournament-${tournament.id}`)?.click()}
                          >
                            <Pencil size={16} className="mr-2" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => deleteTournament(tournament.id)}
                            className="text-red-600"
                          >
                            <Trash2 size={16} className="mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                  <div style={{ display: 'none' }}>
                    <EntityActions
                      entity={tournament}
                      entityType="tournament"
                      onEdit={updateTournament}
                      onDelete={deleteTournament}
                      triggerId={`edit-tournament-${tournament.id}`}
                    />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Start Date</span>
                      <span>{new Date(tournament.startDate).toLocaleDateString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Teams</span>
                      <span>{tournament.teams?.length || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Matches</span>
                      <span>{matchesCount}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox
                    checked={selectedTournaments.length === sortedTournaments.length && sortedTournaments.length > 0}
                    onCheckedChange={handleSelectAll}
                  />
                </TableHead>
                <SortableTableHeader
                  label="Name"
                  sortKey="name"
                  currentSort={sortKey}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  className="min-w-[200px]"
                />
                <SortableTableHeader
                  label="Location"
                  sortKey="location"
                  currentSort={sortKey}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                />
                <SortableTableHeader
                  label="Start Date"
                  sortKey="startDate"
                  currentSort={sortKey}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                />
                <TableHead>Teams</TableHead>
                <TableHead>ID</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedTournaments.map((tournament) => (
                <TableRow key={tournament.id}>
                  <TableCell>
                    <Checkbox
                      checked={selectedTournaments.includes(tournament.id)}
                      onCheckedChange={(checked) => handleSelectTournament(tournament.id, checked as boolean)}
                    />
                  </TableCell>
                  <TableCell className="font-medium flex items-center gap-2">
                    {tournament.logoUrl ? (
                      <img
                        src={tournament.logoUrl}
                        alt={tournament.name + ' logo'}
                        className="w-8 h-8 object-cover rounded-full border border-gray-200 bg-white"
                      />
                    ) : (
                      <div
                        className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-white font-bold text-sm uppercase select-none"
                        style={{ backgroundColor: tournament.logoColor || getRandomColor() }}
                      >
                        {tournament.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                    )}
                    <Link to={`/tournaments/${tournament.id}`} className="hover:underline">
                      {tournament.name}
                    </Link>
                  </TableCell>
                  <TableCell>{tournament.location}</TableCell>
                  <TableCell>{new Date(tournament.startDate).toLocaleDateString()}</TableCell>
                  <TableCell>{tournament.teams?.length || 0}</TableCell>
                  <TableCell>
                    {tournament.uniqueId && <UniqueIdDisplay uniqueId={tournament.uniqueId} />}
                  </TableCell>
                  <TableCell>
                    <div style={{ display: 'none' }}>
                      <EntityActions
                        entity={tournament}
                        entityType="tournament"
                        onEdit={updateTournament}
                        onDelete={deleteTournament}
                        triggerId={`edit-tournament-${tournament.id}`}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
};

export default TournamentsPage;
